import type { Snippet } from '../../types/snippet.ts';

let fetchSnippetsPromise: Promise<Snippet[]> | null = null;
let cachedSnippets: Snippet[] | null = null;

const keyedFetchSnippetsPromises = new Map<string, Promise<Snippet[]>>();
const keyedCachedSnippets = new Map<string, Snippet[]>();

export const withSnippetsCache = (
	fetcher: () => Promise<Snippet[]>,
	cacheKey?: string,
): Promise<Snippet[]> => {
	if (!cacheKey) {
		if (fetchSnippetsPromise) {
			return fetchSnippetsPromise;
		}

		if (cachedSnippets !== null) {
			return Promise.resolve(cachedSnippets);
		}

		fetchSnippetsPromise = (async () => {
			try {
				const result = await fetcher();
				cachedSnippets = result;
				return result;
			} finally {
				fetchSnippetsPromise = null;
			}
		})();

		return fetchSnippetsPromise;
	}

	const existingPromise = keyedFetchSnippetsPromises.get(cacheKey);
	if (existingPromise) {
		return existingPromise;
	}

	const cachedValue = keyedCachedSnippets.get(cacheKey);
	if (cachedValue !== undefined) {
		return Promise.resolve(cachedValue);
	}

	const keyedFetchSnippetsPromise = (async () => {
		try {
			const result = await fetcher();
			keyedCachedSnippets.set(cacheKey, result);
			return result;
		} finally {
			keyedFetchSnippetsPromises.delete(cacheKey);
		}
	})();

	keyedFetchSnippetsPromises.set(cacheKey, keyedFetchSnippetsPromise);
	return keyedFetchSnippetsPromise;
};

/**
 * Clear the snippets cache. This will force the next call to fetchSnippets() to make new HTTP requests.
 */
export const clearSnippetsCache = (): void => {
	cachedSnippets = null;
	fetchSnippetsPromise = null;
	keyedCachedSnippets.clear();
	keyedFetchSnippetsPromises.clear();
};
